import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  RadioGroup,
  Select,
  RadioButton,
  TextField,
  Modal,
  DocumentList,
  DocumentsModal,
  Button,
  DatePicker,
  TextArea,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { CodeBook, UploadFile } from "@king-ict/eupisi-ui/models";
import {
  codeBookToSelectData,
  hrDate,
  getYearFromDate,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { StatusHistoryActionButtonModal } from "./actionModals";
import { CommitteeDecisionWrapper } from "./CommitteeDecision";
import {
  PROCEDURE_SUBMITTED_STATUSES,
  REQUEST_DETAILS_ADMIN_ACCESS,
  REQUEST_DETAILS_INSTITUTION_ACCESS,
  REQUEST_DETAILS_COUNTY_ACCESS,
  ENROLLMENT_INSTITUTION_STATUSES,
  RETURN_TO_SUBMITTED_STATUSES,
  RETURN_TO_PROCEDURE_STATUSES,
  DEFAULT_REQUEST,
  DEFAULT_PERSON_DETAILS,
  REQUEST_DETAILS_RETURN_TO_ACCESS,
} from "./constants";
import { RemarksRedirectModal } from "./modals";
import styles from "./Request.module.scss";
import {
  HasGotMinimumDecisions,
  TabKey,
  PageState,
  PendingAction,
} from "./types";
import { getInstitutionById } from "../../api/Institution/InstitutionApi";
import {
  getRequest,
  getInitialQuestionnaire,
  getRequestDocument,
  approveRequest,
  getCommitteeDecisionList,
  getQuestionnaireExport,
  cancelRequest,
  revertRequest,
  changeRequestType,
  getDecisionProposal,
  getForm7,
  getEnrollmentDecision,
  getMedicalExamination,
  getForm6,
  saveSubsequentDocuments,
  submittedRequest,
  getRequestEnrollmentState,
  suspendProcedure,
} from "../../api/Request/RequestApi";
import { getSchoolYear } from "../../api/SchoolYear/SchoolYearApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestExpandablePanel,
  RequestHeader,
} from "../../components";
import { ConfirmModal } from "../../components/FormControls/ConfirmModal";
import { TextAreaWithCount } from "../../components/Inputs";
import { useDidUpdateEffect } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, Role, SchoolYear } from "../../models";
import { CommitteeRoleEnum, CommitteeTypeEnum } from "../../models/Committee";
import { Institution } from "../../models/Institution";
import {
  DataTypeEnum,
  DisplayTypeEnum,
  Question,
  QuestionGroup,
  RequestAnswer,
} from "../../models/Questionnaire";
import {
  PersonDetails,
  Request,
  RequestStatus,
  CommercialProgram,
  RequestStep,
  RequestType,
  DocumentType,
  Form6,
  InstitutionChangeReason,
  CommitteeDecision,
  DecisionProposal,
  Form7,
  EnrollmentDecision,
  MedicalExamination,
  RequestTypeChange,
  SubsequentDocuments,
  SynchronizationStatus,
  RequestEnrollmentState,
} from "../../models/Request";
import ParentNumberEnum from "../../models/Request/ParentNumberEnum";
import { Attachment } from "../../models/Shared/Attachment";
import { ClaimType } from "../../models/User";
import {
  useGetAlternativeCodeReasonListQuery,
  useGetAlternativeCodeTypeListQuery,
  useGetCitizenshipListQuery,
  useGetContactMethodListQuery,
  useGetCountriesQuery,
  useGetGenderListQuery,
  useGetNationalityListQuery,
  useGetNationalMinorityListQuery,
  useGetParentNumberListQuery,
  useGetParentshipTypeListQuery,
  useGetProfessionalQualificationListQuery,
} from "../../queries";
import {
  requestAdminListRoute,
  requestAdminSchoolCandidateCreateUrl,
  decisionProposalEditUrl,
  form7EditUrl,
  form6EditUrl,
  enrollmentDecisionEditUrl,
  requestInstitutionEditUrl,
  medicalExaminationEditUrl,
  enrollmentInstitutionEditUrl,
  enrollmentFormEditUrl,
} from "../../routes/routes";
import { resetRedirectURL } from "../../slices/applicationSlice";

export function RequestDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { hasRoleAccess } = auth;

  //STORAGE KEY
  const storageKey = "RequestDetailsPageState";
  const referenceBirthday = new Date(2050, 11, 25);
  const defaultPageState: PageState = {
    tabState: TabKey.BasicInfo,
  };

  const createDefaultPageState = (): PageState => {
    const sessionStoragePageState = sessionStorage.getItem(storageKey);
    return sessionStoragePageState
      ? (JSON.parse(sessionStoragePageState) as PageState)
      : defaultPageState;
  };

  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const { data: countries } = useGetCountriesQuery();
  const { data: genders } = useGetGenderListQuery();
  const { data: nationalMinorities } = useGetNationalMinorityListQuery();
  const { data: nationalities } = useGetNationalityListQuery();
  const { data: parentshipTypes } = useGetParentshipTypeListQuery();
  const { data: professionalQualifications } =
    useGetProfessionalQualificationListQuery();
  const { data: parentNumbers } = useGetParentNumberListQuery();
  const { data: contactMethods } = useGetContactMethodListQuery();
  const { data: alternativeCodeReasons } =
    useGetAlternativeCodeReasonListQuery();
  const { data: alternativeCodeTypes } = useGetAlternativeCodeTypeListQuery();
  const { data: citizenships } = useGetCitizenshipListQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [primaryInstitution, setPrimaryInstitution] = useState<Institution>();
  const [secondaryInstitution, setSecondaryInstitution] =
    useState<Institution>();
  const [questionGroups, setQuestionGroups] = useState<QuestionGroup[]>([]);
  const [showUploadModal, setShowUploadModal] = useState<DocumentType>();
  const [uploadDocument, setUploadDocument] = useState<UploadFile[]>([]);
  const [committeeDecisions, setCommitteeDecisions] = useState<
    CommitteeDecision[]
  >([]);

  const [decisionProposal, setDecisionProposal] = useState<DecisionProposal>();
  const [form7, setForm7] = useState<Form7>();
  const [enrollmentDecision, setEnrollmentDecision] =
    useState<EnrollmentDecision>();
  const [medicalExamination, setMedicalExamination] =
    useState<MedicalExamination>();
  const [form6, setForm6] = useState<Form6>();
  const [schoolYear, setSchoolYear] = useState<SchoolYear>();
  const [tabKey, setTabKey] = useState<TabKey>(
    () => createDefaultPageState().tabState
  );
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [confirmMessage, setConfirmMessage] = useState<string>();
  const [confirmMessage2, setConfirmMessage2] = useState<string>();
  const [confirmAction, setConfirmAction] = useState<string>();
  const [actionMessage, setActionMessage] = useState<string>();
  const [changeTypeComment, setChangeTypeComment] = useState<string>();
  const [returnToSubmittedComment, setReturnToSubmittedComment] =
    useState<string>();

  const [changeTypeConfirmState, setChangeTypeConfirmState] = useState<any>();
  const [hasGotMinimumDecisions, setHasGotMinimumDecisions] =
    useState<HasGotMinimumDecisions>({
      county: false,
      institution: false,
    });
  const [enrollmentState, setEnrollmentState] =
    useState<RequestEnrollmentState>({
      isEnrollmentApprovedButtonDisabled: false,
      showModal: false,
      showForm6Button: false,
      showSuspensionDecisionButton: false,
      showRegistrationFormDataChangeButton: false,
    });

  const redirectURL = useTypedSelector(
    (state) => state.application.redirectURL
  );

  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const isStatusNotSuspended =
    item.statusId !== RequestStatus.DecisionSuspended;

  const getCommercialPrograms = (institution: Institution): CodeBook[] => {
    let arrayObjects = [] as CodeBook[];
    for (const [propertyKey, propertyValue] of Object.entries(
      CommercialProgram
    )) {
      const num = Number(propertyKey);
      if (Number.isNaN(Number(propertyKey))) {
        continue;
      }
      const tranKey = "commercialProgram." + propertyValue;
      const trans = t(tranKey);
      arrayObjects.push({ id: num, name: trans });
    }
    if (!(institution.institutionSchoolYearOther?.allDayClass ?? false))
      arrayObjects = arrayObjects.filter(
        (v) => v.id !== CommercialProgram.FullDayClasses
      );
    if (!(institution.institutionSchoolYearOther?.extendedStayClass ?? false))
      arrayObjects = arrayObjects.filter(
        (v) => v.id !== CommercialProgram.ExtendedStay
      );
    return arrayObjects;
  };
  const getInstitutionChangeReasons = (): CodeBook[] => {
    const arrayObjects = [] as CodeBook[];
    for (const [propertyKey, propertyValue] of Object.entries(
      InstitutionChangeReason
    )) {
      const num = Number(propertyKey);
      if (Number.isNaN(Number(propertyKey))) {
        continue;
      }
      const tranKey = "institutionChangeReason." + propertyValue;
      const trans = t(tranKey);
      arrayObjects.push({ id: num, name: trans });
    }
    return arrayObjects;
  };
  const getInstitutionContactInformation = (institution: Institution) => {
    let contactInformationArr = institution.contacts ?? [];
    if (institution.institutionSchoolYearOther?.telephoneNumber) {
      contactInformationArr = contactInformationArr.filter(
        (val) => val.contactType !== "Telefon"
      );
      contactInformationArr.push({
        contactType: "Telefon",
        contactValue: institution.institutionSchoolYearOther.telephoneNumber,
      });
    }
    if (institution.institutionSchoolYearOther?.email) {
      contactInformationArr = contactInformationArr.filter(
        (val) => val.contactType !== "e-mail"
      );
      contactInformationArr.push({
        contactType: "e-mail",
        contactValue: institution.institutionSchoolYearOther.email,
      });
    }
    return (
      <>
        {contactInformationArr.map((val) => (
          <Row>
            <Col md={2}>{val.contactType}</Col>
            <Col md={6}>{val.contactValue}</Col>
            <Col md={4}>{val.note}</Col>
          </Row>
        ))}
      </>
    );
  };
  const getQuestionnaireDisplay = () => {
    if (questionGroups && questionGroups.length > 0) {
      return <>{questionGroups.map(getQuestionnaireGroupDisplay)}</>;
    }
    return <>No groups found.</>;
  };
  const getQuestionnaireGroupDisplay = (group: QuestionGroup) => {
    return (
      <RequestExpandablePanel panelTitle={group.name}>
        <FormCard>
          {group.questions?.map((v, i) => getQuestionnaireQuestionDisplay(v))}
        </FormCard>
      </RequestExpandablePanel>
    );
  };
  const getQuestionnaireQuestionDisplay = (question: Question) => {
    if (
      !question.requiredForAnswerId ||
      item.questionnaireAnswers?.find(
        (v) => v.answerId === question.requiredForAnswerId
      ) !== undefined
    ) {
      switch (question.displayTypeId) {
        case DisplayTypeEnum.Date:
          return (
            <Row>
              <Col md={12}>DatePicker</Col>
            </Row>
          );
        case DisplayTypeEnum.Radio:
          return (
            <Row>
              <Col md={12}>
                <RadioGroup
                  name={question.name}
                  title={question.name}
                  onChange={(e) =>
                    handleQuestionnaireQuestionChange(
                      question.questionGroupQuestionId,
                      undefined,
                      Number.parseInt(e.target.value)
                    )
                  }
                >
                  <Row>
                    {question.answers!.map((a) => (
                      <Col md={4}>
                        <RadioButton
                          ariaLabel={a.name}
                          key={a.id}
                          label={a.name}
                          checked={
                            item.questionnaireAnswers?.find(
                              (v) =>
                                v.questionId ===
                                  question.questionGroupQuestionId &&
                                v.answerId === a.id
                            ) !== undefined
                          }
                          value={a.id}
                          disabled
                        />
                      </Col>
                    ))}
                  </Row>
                </RadioGroup>
              </Col>
            </Row>
          );
        case DisplayTypeEnum.ReadOnly:
          return (
            <Row>
              <Col md={12}>
                <TextField
                  label={question.name}
                  value={
                    item.questionnaireAnswers?.find(
                      (v) => v.questionId === question.questionGroupQuestionId
                    )?.answerText
                  }
                  plaintext
                />
              </Col>
            </Row>
          );
        case DisplayTypeEnum.Select:
          return (
            <Row>
              <Col md={12}>
                <TextField
                  label={question.name}
                  value={
                    question.answers?.find(
                      (v) =>
                        v.id ===
                        item.questionnaireAnswers?.find(
                          (v) =>
                            v.questionId === question.questionGroupQuestionId
                        )?.answerId
                    )?.name
                  }
                  plaintext
                />
              </Col>
            </Row>
          );
        case DisplayTypeEnum.MultiSelect:
          return (
            <Row>
              <Col md={12}>
                <TextField
                  label={question.name}
                  value={question.answers
                    ?.filter(
                      (val) =>
                        item.questionnaireAnswers?.find(
                          (v) =>
                            v.questionId === question.id &&
                            v.answerId === val.id
                        ) !== undefined
                    )
                    .map((v) => v.name)
                    .join()}
                  plaintext
                  readonly
                />
              </Col>
            </Row>
          );
        case DisplayTypeEnum.TextArea:
          return (
            <Row>
              <Col md={12}>TextEditor</Col>
            </Row>
          );
        case DisplayTypeEnum.Time:
          return (
            <Row>
              <Col md={12}>Time</Col>
            </Row>
          );
        case DisplayTypeEnum.Upload:
          return (
            <Row>
              <Col md={12}>Upload</Col>
            </Row>
          );
        default: // Text
          return (
            <Row>
              <Col md={12}>
                <TextField
                  type={
                    question.dataTypeId === DataTypeEnum.Integer
                      ? "number"
                      : "text"
                  }
                  label={question.name}
                  value={
                    item.questionnaireAnswers?.find(
                      (v) => v.questionId === question.id
                    )?.answerText
                  }
                  plaintext
                  readonly
                />
              </Col>
            </Row>
          );
      }
    }
  };
  const filterRequiredAnswers = (answers: RequestAnswer[]): RequestAnswer[] => {
    const questionArr = questionGroups.flatMap((qg) =>
      qg.questions?.filter(
        (q) =>
          q.requiredForAnswerId != null &&
          answers.find((a) => a.answerId === q.requiredForAnswerId) ===
            undefined
      )
    );
    const answerArr = answers.filter(
      (a) =>
        questionArr.find((q) => q?.questionGroupQuestionId === a.questionId) ===
        undefined
    );
    return answerArr;
  };
  const savePageState = () => {
    const pageState: PageState = {
      tabState: tabKey,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(pageState));
  };

  useEffect(() => {
    (async () => {
      const committeeDecisionPromise = getCommitteeDecisionList(+id);
      const questionnairePromise = getInitialQuestionnaire();
      const decisionProposalPromise = getDecisionProposal(+id);
      const form7Promise = getForm7(+id);
      const form6Promise = getForm6(+id);
      const enrollmentDecisionPromise = getEnrollmentDecision(+id);
      const medicalExaminationPromise = getMedicalExamination(+id);
      const enrollmentStatePromise = getRequestEnrollmentState(+id);
      const result = await getRequest(+id);
      if (result) {
        setIsLoading(false);
        setItem(() => result);
      }

      const committeeDecisions = await committeeDecisionPromise;
      if (committeeDecisions) {
        setCommitteeDecisions(committeeDecisions);
      }
      const questionnaire = await questionnairePromise;
      if (questionnaire && questionnaire.questionGroups) {
        setQuestionGroups(() => questionnaire.questionGroups);
      }
      const decisionProposalItem = await decisionProposalPromise;
      if (decisionProposalItem) {
        setDecisionProposal(decisionProposalItem);
      }
      const form7Item = await form7Promise;
      if (form7Item) {
        setForm7(form7Item);
      }
      const form6Item = await form6Promise;
      if (form6Item) {
        setForm6(form6Item);
      }
      const enrollmentDecisionItem = await enrollmentDecisionPromise;
      if (enrollmentDecisionItem) {
        setEnrollmentDecision(enrollmentDecisionItem);
      }
      const medicalExaminationItem = await medicalExaminationPromise;
      if (medicalExaminationItem) {
        setMedicalExamination(medicalExaminationItem);
      }
      const enrollmentState = await enrollmentStatePromise;
      if (enrollmentState) {
        setEnrollmentState(enrollmentState);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (item && item.schoolYearId && item.schoolYearId > 0) {
        const schoolYear = await getSchoolYear(item.schoolYearId);
        setSchoolYear(schoolYear);

        if (
          item.institutionId &&
          (!primaryInstitution || primaryInstitution.id !== item.institutionId)
        ) {
          const i = await getInstitutionById(
            item.institutionId,
            item.schoolYearId
          );
          if (i) setPrimaryInstitution(i);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.institutionId, item.schoolYearId]);
  useEffect(() => {
    (async () => {
      if (
        item.outsideEnrollmentAreaInstitutionProgram?.institutionId !==
          undefined &&
        item.schoolYearId > 0 &&
        (!secondaryInstitution ||
          secondaryInstitution.id !==
            item.outsideEnrollmentAreaInstitutionProgram.institutionId)
      ) {
        const i = await getInstitutionById(
          item.outsideEnrollmentAreaInstitutionProgram.institutionId,
          item.schoolYearId
        );
        if (i) setSecondaryInstitution(i);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.outsideEnrollmentAreaInstitutionProgram, item.schoolYearId]);
  useEffect(() => {
    if (errors === undefined || errors.length === 0) {
      switch (confirmAction) {
        case "approve":
          setActionMessage(t("request.requestApproved"));
          break;
        case "cancel":
          setActionMessage(t("request.requestCanceled"));
          break;
        case "returnToProcessing":
          setActionMessage(t("request.returnedToProcessing"));
          break;
        case "submitted":
          setActionMessage(t("request.changedType"));
          break;
      }
    }
  }, [errors]);
  useDidUpdateEffect(() => {
    // At the end of processing when nothing has changed, remove the storage item.
    if (tabKey) sessionStorage.removeItem(storageKey);
  }, [tabKey]);

  const handleParentOneProfessionNameChange = (e: any) => {
    const val = e.target.value as string;
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      professionName: val && val.length > 0 ? val : null,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOnePhoneNumberChange = (e: any) => {
    const val = e.target.value as string;
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      phoneNumber: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOneEmailChange = (e: any) => {
    const val = e.target.value as string;
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      email: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoDocumentNumberChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? DEFAULT_PERSON_DETAILS;
    const p: PersonDetails = {
      ...pd,
      person: {
        ...pd.person,
        alternativeDocumentNumber: val && val.length > 0 ? val : null,
      },
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoLastNameChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? DEFAULT_PERSON_DETAILS;
    const p: PersonDetails = {
      ...pd,
      lastName: val && val.length > 0 ? val : "",
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoFirstNameChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? DEFAULT_PERSON_DETAILS;
    const p: PersonDetails = {
      ...pd,
      firstName: val && val.length > 0 ? val : "",
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoCustodyChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? DEFAULT_PERSON_DETAILS;
    const p: PersonDetails = {
      ...pd,
      custody: val && val.length > 0 ? val : null,
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleInstitutionChangeReasonOtherChange = (e: any) => {
    const val = e.target.value as string;
    const r: Request = {
      ...item,
      institutionChangeReasonOther: val,
    };
    handleUpdate(r);
  };
  const handleQuestionnaireQuestionChange = (
    id: number,
    val?: string,
    answerId?: number
  ) => {
    const requestAnswer: RequestAnswer = item.questionnaireAnswers?.find(
      (v) => v.questionId === id
    ) ?? { questionId: id };
    const newRequestAnswer = {
      ...requestAnswer,
      answerId: answerId,
      answerText: val,
    };
    let arr =
      item.questionnaireAnswers?.filter((v) => v.questionId !== id) ?? [];
    arr.push(newRequestAnswer);
    arr = filterRequiredAnswers(arr);
    const r: Request = {
      ...item,
      questionnaireAnswers: arr,
    };
    handleUpdate(r);
  };
  //ovo doslovno ništa ne radi
  const handleFileChange = async (files: File[]) => {
    const uploadedFiles: UploadFile[] = [];
    if (files && files.length > 0) {
      files.forEach((file) => {
        const fileReader = new FileReader();
        fileReader.readAsBinaryString(file);
        const uploadedFile = {
          fileName: file.name,
          fileData: fileReader.result,
        } as UploadFile;
        uploadedFiles.push(uploadedFile);
      });
    }
    setUploadDocument(uploadedFiles.length > 0 ? uploadedFiles : []);
  };
  const handleUploadFileChange = (files: UploadFile[]) => {
    const newItem: Request = {
      ...item,
    };

    const combined = files.concat(uploadDocument);
    setUploadDocument(combined ?? []);

    if (showUploadModal === DocumentType.Subsequent) {
      newItem.subsequentUploadFiles =
        combined && combined.length > 0 ? combined : null;
    } else {
      switch (item.step) {
        case RequestStep.ParentData:
          if ((item.subStep ?? 0) === 1)
            newItem.otherUploadFiles =
              combined && combined.length > 0 ? combined : null;
          break;
      }
    }

    setShowUploadModal(undefined);
    handleUpdate(newItem);
  };
  const handleSuspensionDecisionClicked = async () => {
    setConfirmAction("suspensionDecision");
    setConfirmMessage(t("request.suspensionDecisionConfirmation"));
  };
  const handleAddSchoolCandidateClicked = () => {
    history.push(requestAdminSchoolCandidateCreateUrl(item.id));
  };
  const handleSelectInstitutionDataClicked = () => {
    history.push(requestInstitutionEditUrl(item.id));
  };
  const handleGenerateDecisionProposalClicked = () => {
    if (enrollmentState.showModal) {
      setShowRemarksModal(true);
      setPendingAction("generateDecisionProposal");
      return;
    }

    navigateToDecisionProposal();
  };
  const handleGenerateForm7Clicked = () => {
    history.push(form7EditUrl(item.id));
  };
  const handleGenerateForm6Clicked = () => {
    history.push(form6EditUrl(item.id));
  };
  const handleEnrollmentDecisionClicked = () => {
    history.push(enrollmentDecisionEditUrl(item.id));
  };
  const handleEnrollmentInstitutionClicked = () => {
    history.push(enrollmentInstitutionEditUrl(item.id));
  };

  const handleUpdate = (request: Request) => {
    setItem(request);
  };
  const handleApproveClicked = async () => {
    if (enrollmentState.showModal) {
      setShowRemarksModal(true);
      setPendingAction("approve");
      return;
    }

    showApproveConfirmation();
  };
  const showApproveConfirmation = () => {
    setConfirmAction("approve");
    setConfirmMessage(t("request.approveConfirmation"));
  };
  const handleCancelClicked = async () => {
    setConfirmAction("cancel");
    setConfirmMessage(t("request.cancelConfirmation"));
  };
  const handleReturnToProcessingClicked = async () => {
    setConfirmAction("returnToProcessing");
    setConfirmMessage(t("request.returnToProcessingConfirmation"));
  };
  const handleSubmittedClicked = () => {
    setConfirmAction("submitted");
    if (!RETURN_TO_PROCEDURE_STATUSES.includes(item.statusId)) {
      setConfirmMessage(t("request.submittedConfirmation"));
      setConfirmMessage2(t("request.submittedConfirmation1"));
    } else if (item.statusId !== RequestStatus.OnSchoolCandidateList) {
      setConfirmMessage(t("request.procedureSubmittedConfirmation"));
    } else {
      setConfirmMessage(t("request.deleteSchoolCandidateConfirmation"));
      setConfirmMessage2(t("request.deleteSchoolCandidateConfirmation1"));
    }
  };
  const handleChangeRequestType = async () => {
    setConfirmAction("changeType");
    setChangeTypeConfirmState("confirm");
  };
  const handleDocumentDownload = async (
    doc: Attachment,
    type: DocumentType
  ) => {
    if (!doc?.id) return;

    await getRequestDocument(doc.id, item.id, type);
  };
  const handleSubsequentUploadDocumentClick = () => {
    setShowUploadModal(DocumentType.Subsequent);
  };
  const handleSubsequentDocumentsSave = async () => {
    const requestItem: SubsequentDocuments = {
      requestId: item.id,
      uploadedFiles: item.subsequentUploadFiles ?? [],
      attachments: item.subsequentAttachments ?? [],
    };

    const result = await saveSubsequentDocuments(requestItem);

    const newItem: Request = {
      ...item,
      subsequentAttachments: result,
      subsequentUploadFiles: [],
    };

    setUploadDocument([]);

    handleUpdate(newItem);
  };

  const handleRemoveSubsequentAttachment = (doc: Attachment) => {
    if (!doc?.id) return;

    const newItem: Request = {
      ...item,
    };

    const subsequentAttachments = item.subsequentAttachments?.filter(
      (v) => v.id !== doc.id
    );
    newItem.subsequentAttachments =
      subsequentAttachments && subsequentAttachments.length > 0
        ? subsequentAttachments
        : null;

    handleUpdate(newItem);
  };

  const handleRemoveSubsequentUploadFile = (doc: UploadFile) => {
    if (!doc?.fileName) return;

    const newItem: Request = {
      ...item,
    };

    const subsequentUploadFiles = item.subsequentUploadFiles?.filter(
      (v) => v.fileName !== doc.fileName
    );
    newItem.subsequentUploadFiles =
      subsequentUploadFiles && subsequentUploadFiles.length > 0
        ? subsequentUploadFiles
        : null;

    handleUpdate(newItem);
  };

  const handleGetQuestionnaireClicked = async () => {
    await getQuestionnaireExport(item.id);
  };
  const handleMedicalExaminationDetailsClicked = async (id: number) => {
    savePageState();
    history.push(medicalExaminationEditUrl(+id));
  };
  const doSuspensionDecision = async () => {
    await suspendProcedure(item.id);
  };
  const handleConfirmOk = async () => {
    setConfirmMessage(undefined);
    setConfirmMessage2(undefined);
    if (confirmAction === "approve") {
      await doApprove();
    }
    if (confirmAction === "cancel") {
      await doCancel();
    }
    if (confirmAction === "returnToProcessing") {
      await doRevertToDraft();
    }
    if (confirmAction === "changeType") {
      await doChangeRequestType();
    }
    if (confirmAction === "submitted") {
      if (!returnToSubmittedComment?.trim()) {
        return;
      }
      await doSubmitted();
    }
    if (confirmAction === "suspensionDecision") {
      await doSuspensionDecision();
      window.location.reload();
    }
    setReturnToSubmittedComment(undefined);
  };
  const handleConfirmCancel = () => {
    setConfirmAction(undefined);
    setConfirmMessage(undefined);
    setReturnToSubmittedComment(undefined);
    setChangeTypeConfirmState(undefined);
  };
  const doApprove = async () => {
    const result = await approveRequest(item.id);
    if (result) {
      setErrors(() => result.validationFailures);
      if (result.isValid && result.result) setItem(result.result);
    }
  };
  const doCancel = async () => {
    const result = await cancelRequest(item.id);
    if (result) {
      setErrors(result.validationFailures);
      if (result.isValid && result.result) setItem(result.result);
    }
  };
  const doRevertToDraft = async () => {
    const result = await revertRequest(item.id);
    setErrors(() => result?.validationFailures);
  };
  const doChangeRequestType = async () => {
    const requestTypeChange: RequestTypeChange = {
      requestId: item.id,
      comment: changeTypeComment,
    };
    const result = await changeRequestType(requestTypeChange);
    if (!result) return;
    setErrors(() => result.validationFailures);
    if (result.isValid && result.result) {
      setItem(result.result);
      setChangeTypeConfirmState("message");
      setChangeTypeComment(undefined);
      const enrollmentState = await getRequestEnrollmentState(item.id);
      setEnrollmentState(enrollmentState);
    } else {
      setChangeTypeConfirmState("error");
    }
  };
  const doSubmitted = async () => {
    const result = await submittedRequest(
      item.id,
      returnToSubmittedComment || ""
    );
    if (!result) return;

    setErrors(result.validationFailures);
    if (result.isValid && result.result) {
      setItem(result.result);
      setForm7(undefined);
      setEnrollmentDecision(undefined);
      const canDeleteForm6 =
        item.type === RequestType.EarlyEnrollment &&
        item.statusId === RequestStatus.OnSchoolCandidateList;
      if (canDeleteForm6) {
        setForm6(undefined);
      }
      window.location.reload();
    }
  };
  const handleMessageOK = () => {
    if (confirmAction === "returnToProcessing")
      history.push(requestAdminListRoute);

    setActionMessage(undefined);
    setConfirmAction(undefined);
  };
  const goBack = () => {
    if (redirectURL) {
      dispatch(resetRedirectURL());
      history.push(redirectURL);
    } else history.goBack();
  };
  const getElectiveSubjectsString = (institutionId?: number): string => {
    if (institutionId)
      return (
        item.electiveSubjects
          ?.filter((v) => v.parentId === institutionId)
          .map((v) => v.name)
          .join() ?? ""
      );
    return "";
  };
  const getPageTitle = (): string => {
    let typeString;
    switch (item.type) {
      case RequestType.EarlyEnrollment:
        typeString = t("request.requestTitleEarlyEnrollment");
        break;
      case RequestType.RegularEnrollment:
        typeString = t("request.requestTitleRegularEnrollment");
        break;
      case RequestType.RegularEnrollmentForChildrenWithLearningDifficulties:
        typeString = t(
          "request.requestTitleRegularEnrollmentForChildrenWithLearningDifficulties"
        );
        break;
      case RequestType.TemporaryExemption:
        typeString = t("request.requestTitleTemporaryExemption");
        break;
    }
    typeString += item.schoolYearCode ? item.schoolYearCode! : "";
    return typeString;
  };
  const getInstitutionCommitteeTabTitle = () => {
    if (
      item.statusId === RequestStatus.ProcedureSubmitted &&
      !hasGotMinimumDecisions.institution &&
      !isLoading
    )
      return (
        <span className="text-danger">
          *
          {t(
            "common.requestModel.requestTabs.institutionCommitteeDecisionInfo"
          )}
        </span>
      );

    return (
      <>
        {t("common.requestModel.requestTabs.institutionCommitteeDecisionInfo")}
      </>
    );
  };
  const getCountyCommitteeTabTitle = () => {
    if (
      item.statusId === RequestStatus.OpinionWithRecommendation &&
      !hasGotMinimumDecisions.county &&
      !isLoading
    )
      return (
        <span className="text-danger">
          *{t("common.requestModel.requestTabs.countyCommitteeDecisionInfo")}
        </span>
      );
    return (
      <>{t("common.requestModel.requestTabs.countyCommitteeDecisionInfo")}</>
    );
  };

  const canShowTabCommitteeDecisionsInstitution = () => {
    if (!item || isLoading) return false;
    return (
      !PROCEDURE_SUBMITTED_STATUSES.includes(item.statusId) &&
      item.type !== RequestType.TemporaryExemption
    );
  };
  const canShowTabCommitteeDecisionsCounty = () => {
    if (!item || isLoading) return false;
    return (
      (item.statusId !== RequestStatus.ProcedureSubmitted &&
        item.statusId !== RequestStatus.OnSchoolCandidateList &&
        item.statusId !== RequestStatus.Cancelled) ||
      item.type === RequestType.TemporaryExemption
    );
  };
  const canShowTabQuestionary = () => {
    if (!item || isLoading) return false;
    return (
      item.type === RequestType.RegularEnrollment ||
      item.type ===
        RequestType.RegularEnrollmentForChildrenWithLearningDifficulties ||
      (item.type === RequestType.EarlyEnrollment &&
        item.questionnaireAnswers != null)
    );
  };
  const canShowTabInstitution = () => {
    if (!item || isLoading) return false;
    return (
      item.type === RequestType.RegularEnrollment ||
      item.type ===
        RequestType.RegularEnrollmentForChildrenWithLearningDifficulties ||
      (item.type === RequestType.EarlyEnrollment &&
        item.statusId !== RequestStatus.ProcedureSubmitted)
    );
  };
  const userIsCommitteePresident = useMemo(() => {
    if (!auth.currentRole?.committeeMemberships) return false;

    const result = auth.currentRole?.committeeMemberships.find((v) => {
      if (v.committeeType === CommitteeTypeEnum.RegionalCommittee) {
        return (
          v.isPresident &&
          v?.regionalCommitteeInstitutionIds?.includes(item.institutionId ?? 0)
        );
      }

      return (
        v.committeeType === CommitteeTypeEnum.Institution &&
        v.institutionId === item.institutionId &&
        v.isPresident
      );
    });
    return result;
  }, [auth.currentRole?.committeeMemberships, item.institutionId]);

  const navigateToDecisionProposal = () => {
    history.push(decisionProposalEditUrl(item.id));
  };

  const handleRemarksModalConfirm = () => {
    // When user clicks "Yes", redirect to the committee decisions tab
    // and cancel any pending action
    setShowRemarksModal(false);
    setTabKey(TabKey.InstitutionCommitteeDecisionInfo);
    setPendingAction(null);
  };

  const handleRemarksModalClose = () => {
    setShowRemarksModal(false);

    // When user clicks "No", proceed with the original action
    switch (pendingAction) {
      case "generateDecisionProposal":
        navigateToDecisionProposal();
        break;
      case "approve":
        showApproveConfirmation();
        break;
    }

    setPendingAction(null);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={getPageTitle()}
        onClickBack={goBack}
        errors={errors}
      />
      <RequestHeader request={item}></RequestHeader>
      <Tabs
        id="controlled-tab-example"
        activeKey={tabKey ?? TabKey.BasicInfo}
        onSelect={(k) => setTabKey((k as TabKey) ?? TabKey.BasicInfo)}
      >
        <Tab
          title={t("common.requestModel.requestTabs.basicInfo")}
          eventKey={TabKey.BasicInfo}
        >
          <FormCard>
            <RequestExpandablePanel panelTitle={t("request.parentSubStepOne")}>
              <FormCard cardTitle={t("request.basicInformation")}>
                <Row>
                  <Col md={4}>
                    <TextField
                      label={t("person.firstName")}
                      helperText={t("common.birthRegistry")}
                      value={item?.applicantParentDetails?.firstName}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={t("person.lastName")}
                      helperText={t("common.birthRegistry")}
                      value={item?.applicantParentDetails?.lastName}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.oib")}
                      helperText={t("common.oibApi")}
                      value={item?.applicantParentDetails?.person.oib}
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={t("person.dateOfBirth")}
                      helperText={t("common.birthRegistry")}
                      value={
                        item?.applicantParentDetails
                          ? hrDate(
                              new Date(item?.applicantParentDetails.dateOfBirth)
                            )
                          : ""
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={t("person.sex")}
                      helperText={t("common.birthRegistry")}
                      value={
                        genders?.find(
                          (v) => v.id === item?.applicantParentDetails?.genderId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={t("person.birthplace")}
                      helperText={t("common.birthRegistry")}
                      value={item?.applicantParentDetails?.cityOfBirth ?? ""}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={t("person.countryBirth")}
                      helperText={t("common.birthRegistry")}
                      value={
                        countries?.find(
                          (v) =>
                            v.id ===
                            item?.applicantParentDetails?.countryOfBirthId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.citizenship")}
                      helperText={t("common.birthRegistry")}
                      value={
                        citizenships?.find(
                          (v) =>
                            v.id === item?.applicantParentDetails?.citizenshipId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.nationalMinority")}
                      value={
                        nationalMinorities?.find(
                          (v) =>
                            v.id ===
                            item?.applicantParentDetails?.nationalMinorityId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.nationality")}
                      value={
                        nationalities?.find(
                          (v) =>
                            v.id === item?.applicantParentDetails?.nationalityId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.parentshipType")}
                      value={
                        parentshipTypes?.find(
                          (v) =>
                            v.id ===
                            item?.applicantParentDetails?.parentshipTypeId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.professionalQualification")}
                      value={
                        professionalQualifications?.find(
                          (v) =>
                            v.id ===
                            item?.applicantParentDetails
                              ?.professionalQualificationId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.profession")}
                      helperText={t("common.profession")}
                      value={item?.applicantParentDetails?.professionName ?? ""}
                      onChange={handleParentOneProfessionNameChange}
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.phoneNumber")}
                      name={"phoneNumber"}
                      value={item?.applicantParentDetails?.phoneNumber ?? ""}
                      onChange={handleParentOnePhoneNumberChange}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.email")}
                      name={"email"}
                      value={item?.applicantParentDetails?.email ?? ""}
                      onChange={handleParentOneEmailChange}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.parentNumber")}
                      value={
                        parentNumbers?.find(
                          (v) =>
                            v.id ===
                            item?.applicantParentDetails?.parentNumberId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <TextField
                      type="text"
                      label={t("person.contactMethod")}
                      value={
                        contactMethods?.find(
                          (v) =>
                            v.id ===
                            item?.applicantParentDetails?.contactMethodId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
              </FormCard>
              <FormCard cardTitle={t("person.permanentResidenceInformation")}>
                <Row>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.country"
                      )}`}
                      value={
                        countries?.find(
                          (v) =>
                            v.id ===
                            item?.applicantParentDetails?.residence?.countryId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.municipality"
                      )}`}
                      value={
                        item?.applicantParentDetails?.residence
                          ?.permanentAddress?.municipality ??
                        item?.applicantParentDetails?.residence?.address ??
                        ""
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.town"
                      )}`}
                      value={
                        item?.applicantParentDetails?.residence
                          ?.permanentAddress?.town
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.postalCode"
                      )}`}
                      value={
                        item?.applicantParentDetails?.residence
                          ?.permanentAddress?.postalCode
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.street"
                      )}`}
                      value={
                        item?.applicantParentDetails?.residence
                          ?.permanentAddress?.street
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.streetNumber"
                      )}`}
                      value={
                        item?.applicantParentDetails?.residence
                          ?.permanentAddress?.houseNumber ?? undefined
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
              </FormCard>
              {item?.applicantParentDetails?.residence?.temporaryAddress && (
                <FormCard cardTitle={t("person.temporaryResidenceInformation")}>
                  <Row>
                    <Col md={4} />
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.municipality"
                        )}`}
                        value={
                          item?.applicantParentDetails?.residence
                            ?.temporaryAddress?.municipality
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.town"
                        )}`}
                        value={
                          item?.applicantParentDetails?.residence
                            ?.temporaryAddress?.town
                        }
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.postalCode"
                        )}`}
                        value={
                          item?.applicantParentDetails?.residence
                            ?.temporaryAddress?.postalCode
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.street"
                        )}`}
                        value={
                          item?.applicantParentDetails?.residence
                            ?.temporaryAddress?.street
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.streetNumber"
                        )}`}
                        value={
                          item?.applicantParentDetails?.residence
                            ?.temporaryAddress?.houseNumber ?? undefined
                        }
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                </FormCard>
              )}
            </RequestExpandablePanel>
            {item?.applicantParentDetails?.parentNumberId ===
              ParentNumberEnum.TwoParents && (
              <RequestExpandablePanel
                panelTitle={t("request.parentSubStepTwo")}
              >
                <FormCard cardTitle={t("request.basicInformation")}>
                  <Row>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("common.schoolCandidateModel.alternativeCode")}
                        value={
                          item.otherParentDetails?.person
                            .alternativeDocumentNumber ?? ""
                        }
                        onChange={handleParentTwoDocumentNumberChange}
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t(
                          "common.schoolCandidateModel.schoolCandidateAlternativeCodeType"
                        )}
                        value={
                          alternativeCodeTypes?.find(
                            (v) =>
                              v.id ===
                              item.otherParentDetails?.person
                                .alternativeDocumentTypeId
                          )?.name
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t(
                          "common.schoolCandidateModel.schoolCandidateAlternativeCodeReason"
                        )}
                        value={
                          alternativeCodeReasons?.find(
                            (v) =>
                              v.id ===
                              item.otherParentDetails?.person
                                .alternativeDocumentReasonId
                          )?.name
                        }
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <TextField
                        label={t("common.requestModel.parentLastName")}
                        name={"item.parent.lastName"}
                        value={item.otherParentDetails?.lastName}
                        onChange={handleParentTwoLastNameChange}
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={t("common.requestModel.parentFirstName")}
                        name={"item.parent.firstName"}
                        value={item.otherParentDetails?.firstName}
                        onChange={handleParentTwoFirstNameChange}
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={t("common.requestModel.parentOib")}
                        name={"item.parent.oib"}
                        value={item.otherParentDetails?.person.oib}
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <TextField
                        label={t("person.yearOfBirth")}
                        name={"item.parent.oib"}
                        value={
                          item.otherParentDetails &&
                          getYearFromDate(
                            new Date(item.otherParentDetails?.dateOfBirth)
                          ) !== getYearFromDate(referenceBirthday)
                            ? getYearFromDate(
                                new Date(item.otherParentDetails?.dateOfBirth)
                              )
                            : ""
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={t("person.professionalQualification")}
                        value={
                          professionalQualifications?.find(
                            (v) =>
                              v.id ===
                              item?.otherParentDetails
                                ?.professionalQualificationId
                          )?.name
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.profession")}
                        value={item?.otherParentDetails?.professionName ?? ""}
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.phoneNumber")}
                        name={"phoneNumber"}
                        value={item?.otherParentDetails?.phoneNumber ?? ""}
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.email")}
                        value={item?.otherParentDetails?.email ?? ""}
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.parentshipType")}
                        value={
                          parentshipTypes?.find(
                            (v) =>
                              v.id ===
                              item?.otherParentDetails?.parentshipTypeId
                          )?.name
                        }
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <TextField
                        type="text"
                        label={`${t("person.custodian")}`}
                        name={"custody"}
                        value={item?.otherParentDetails?.custody ?? ""}
                        onChange={handleParentTwoCustodyChange}
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                </FormCard>
              </RequestExpandablePanel>
            )}
            <RequestExpandablePanel panelTitle={t("request.childStep")}>
              <FormCard cardTitle={t("request.basicInformation")}>
                <Row>
                  <Col md={4}>
                    <TextField
                      label={t("person.firstName")}
                      helperText={t("common.birthRegistry")}
                      value={item?.childDetails?.firstName}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={t("person.lastName")}
                      helperText={t("common.birthRegistry")}
                      value={item?.childDetails?.lastName}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.oib")}
                      helperText={t("common.oibApi")}
                      value={item?.childDetails?.person.oib}
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={t("person.dateOfBirth")}
                      helperText={t("common.birthRegistry")}
                      value={
                        item?.childDetails
                          ? hrDate(new Date(item?.childDetails.dateOfBirth))
                          : ""
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={t("person.sex")}
                      helperText={t("common.birthRegistry")}
                      value={
                        genders?.find(
                          (v) => v.id === item?.childDetails?.genderId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={t("person.birthplace")}
                      helperText={t("common.birthRegistry")}
                      value={item?.childDetails?.cityOfBirth ?? ""}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={t("person.countryBirth")}
                      helperText={t("common.birthRegistry")}
                      value={
                        countries?.find(
                          (v) => v.id === item?.childDetails?.countryOfBirthId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.citizenship")}
                      helperText={t("common.birthRegistry")}
                      value={
                        citizenships?.find(
                          (v) => v.id === item?.childDetails?.citizenshipId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.nationalMinority")}
                      value={
                        nationalMinorities?.find(
                          (v) => v.id === item?.childDetails?.nationalMinorityId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      type="text"
                      label={t("person.nationality")}
                      value={
                        nationalities?.find(
                          (v) => v.id === item?.childDetails?.nationalityId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
              </FormCard>
              <FormCard cardTitle={t("person.permanentResidenceInformation")}>
                <Row>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.country"
                      )}`}
                      value={
                        countries?.find(
                          (v) =>
                            v.id === item?.childDetails?.residence?.countryId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.municipality"
                      )}`}
                      value={
                        item?.childDetails?.residence?.permanentAddress
                          ?.municipality ??
                        item?.childDetails?.residence?.address ??
                        ""
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.town"
                      )}`}
                      value={
                        item?.childDetails?.residence?.permanentAddress?.town
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.postalCode"
                      )}`}
                      value={
                        item?.childDetails?.residence?.permanentAddress
                          ?.postalCode
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.street"
                      )}`}
                      value={
                        item?.childDetails?.residence?.permanentAddress?.street
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={`${t("person.permanentResidence")} - ${t(
                        "person.streetNumber"
                      )}`}
                      value={
                        item?.childDetails?.residence?.permanentAddress
                          ?.houseNumber ?? undefined
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
              </FormCard>
              {item?.childDetails?.residence?.temporaryAddress && (
                <FormCard cardTitle={t("person.temporaryResidenceInformation")}>
                  <Row>
                    <Col md={4} />
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.municipality"
                        )}`}
                        value={
                          item?.childDetails?.residence?.temporaryAddress
                            ?.municipality
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.town"
                        )}`}
                        value={
                          item?.childDetails?.residence?.temporaryAddress?.town
                        }
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.postalCode"
                        )}`}
                        value={
                          item?.childDetails?.residence?.temporaryAddress
                            ?.postalCode
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.street"
                        )}`}
                        value={
                          item?.childDetails?.residence?.temporaryAddress
                            ?.street
                        }
                        plaintext
                        bold
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.streetNumber"
                        )}`}
                        value={
                          item?.childDetails?.residence?.temporaryAddress
                            ?.houseNumber ?? undefined
                        }
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                </FormCard>
              )}
            </RequestExpandablePanel>
            {item?.parentRemark && (
              <RequestExpandablePanel panelTitle={t("request.parentRemark")}>
                <FormCard>
                  <p className="text-break">{item.parentRemark}</p>
                </FormCard>
              </RequestExpandablePanel>
            )}
            {isStatusNotSuspended &&
              enrollmentState.showRegistrationFormDataChangeButton && (
                <Button
                  size="sm"
                  onClick={() => history.push(enrollmentFormEditUrl(item.id))}
                  disabled={!item.id}
                  className="mt-2"
                >
                  {t("request.editEnrollmentForm")}
                </Button>
              )}
          </FormCard>
        </Tab>
        {canShowTabInstitution() && (
          <Tab
            title={t("common.requestModel.requestTabs.institutionInfo")}
            eventKey={TabKey.InstitutionInfo}
          >
            <FormCard cardTitle={t("request.institutionStep")}>
              <FormCard cardTitle={t("common.expertCommission.institution")}>
                <Row>
                  <Col md={6}>
                    <TextField
                      label={t("common.institutionDetails.name")}
                      value={primaryInstitution?.name}
                      plaintext
                      bold
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <TextField
                      label={t("common.institutionDetails.countyName")}
                      value={primaryInstitution?.address?.county}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={t("common.institutionDetails.municipalityName")}
                      value={primaryInstitution?.address?.municipality}
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={t("common.institutionDetails.townName")}
                      value={primaryInstitution?.address?.town}
                      plaintext
                      bold
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <TextField
                      label={t("request.institutionProgram")}
                      value={
                        primaryInstitution?.programs?.find(
                          (v) => v.id === item.institutionProgramId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  {primaryInstitution &&
                    primaryInstitution.programs.find(
                      (v) => v.id === item.institutionProgram?.id
                    )?.isInternationalProgram && (
                      <Col md={6}>
                        <TextField
                          label={t("common.institutionDetails.straniJezici")}
                          value={
                            item.foreignLanguages?.find(
                              (v) => v.parentId === item.institutionId
                            )?.name
                          }
                          plaintext
                          bold
                        />
                      </Col>
                    )}
                  {primaryInstitution &&
                    item.institutionProgram &&
                    item.languageSchoolingModels?.some(
                      (v) => v.parentId === primaryInstitution.id
                    ) && (
                      <Col md={6}>
                        <TextField
                          label={t("request.languageSchoolingModel")}
                          value={item.languageSchoolingModels
                            ?.filter(
                              (v) => v.parentId === primaryInstitution.id
                            )
                            .map((v) => v.name)
                            .join(", ")}
                          plaintext
                          bold
                        />
                      </Col>
                    )}
                </Row>
                {item.commercialProgramId && primaryInstitution && (
                  <Row>
                    <Col md={6}>
                      <TextField
                        label={t("request.commercialProgram")}
                        value={
                          getCommercialPrograms(primaryInstitution!)?.find(
                            (v) => v.id === item.commercialProgramId
                          )?.name ?? "-"
                        }
                        plaintext
                        bold
                      />
                    </Col>
                  </Row>
                )}
              </FormCard>
              <FormCard cardTitle={t("request.programSelection")}>
                <Row>
                  <Col md={6}>
                    <TextField
                      label={t("common.institutionDetails.obavezniStraniJezik")}
                      value={
                        item.mandatoryForeignLanguages?.find(
                          (v) => v.parentId === item.institutionId
                        )?.name
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <TextField
                      label={t("common.institutionDetails.izborniPredmeti")}
                      value={getElectiveSubjectsString(primaryInstitution?.id)}
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
              </FormCard>
              <FormCard cardTitle={t("request.contactInformation")}>
                {primaryInstitution
                  ? getInstitutionContactInformation(primaryInstitution)
                  : ""}
              </FormCard>
              <FormCard cardTitle={t("request.otherInstitution")}>
                {item.isOutsideEnrollmentArea && (
                  <>
                    <FormCard
                      cardTitle={t("common.expertCommission.institution")}
                    >
                      <Row>
                        <Col md={6}>
                          <TextField
                            label={t("request.institutionChangeReason")}
                            value={
                              getInstitutionChangeReasons().find(
                                (v) => v.id === item.institutionChangeReason
                              )?.name
                            }
                            plaintext
                            bold
                          />
                        </Col>
                        {item.institutionChangeReason ===
                          InstitutionChangeReason.Other && (
                          <Col md={6}>
                            <TextField
                              label={t("request.institutionChangeReason")}
                              value={item.institutionChangeReasonOther ?? ""}
                              onChange={
                                handleInstitutionChangeReasonOtherChange
                              }
                              plaintext
                              bold
                            />
                          </Col>
                        )}
                      </Row>
                      <Row>
                        <Col md={6}>
                          <TextField
                            label={t("common.institutionDetails.name")}
                            value={secondaryInstitution?.name}
                            plaintext
                            bold
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <TextField
                            label={t("common.institutionDetails.countyName")}
                            value={secondaryInstitution?.address?.county}
                            plaintext
                            bold
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={t(
                              "common.institutionDetails.municipalityName"
                            )}
                            value={secondaryInstitution?.address?.municipality}
                            plaintext
                            bold
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={t("common.institutionDetails.townName")}
                            value={secondaryInstitution?.address?.town}
                            plaintext
                            bold
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <TextField
                            label={t("request.institutionProgram")}
                            value={
                              secondaryInstitution?.programs?.find(
                                (v) =>
                                  v.id ===
                                  item.outsideEnrollmentAreaInstitutionProgramId
                              )?.name
                            }
                            plaintext
                            bold
                          />
                        </Col>
                        {secondaryInstitution &&
                          secondaryInstitution.programs.find(
                            (v) =>
                              v.id ===
                              item.outsideEnrollmentAreaInstitutionProgram?.id
                          )?.isInternationalProgram && (
                            <Col md={6}>
                              <TextField
                                label={t(
                                  "common.institutionDetails.straniJezici"
                                )}
                                value={
                                  item.foreignLanguages?.find(
                                    (v) =>
                                      v.parentId === secondaryInstitution.id
                                  )?.name
                                }
                                plaintext
                                bold
                              />
                            </Col>
                          )}
                        {secondaryInstitution &&
                          item.outsideEnrollmentAreaInstitutionProgram &&
                          item.languageSchoolingModels?.some(
                            (v) => v.parentId === secondaryInstitution.id
                          ) && (
                            <Col md={6}>
                              <TextField
                                label={t("request.languageSchoolingModel")}
                                value={item.languageSchoolingModels
                                  ?.filter(
                                    (v) =>
                                      v.parentId === secondaryInstitution.id
                                  )
                                  .map((v) => v.name)
                                  .join(", ")}
                                plaintext
                                bold
                              />
                            </Col>
                          )}
                      </Row>
                      {item.outsideEnrollmentAreaCommercialProgramId &&
                        secondaryInstitution && (
                          <Row>
                            <Col md={6}>
                              <TextField
                                label={t("request.commercialProgram")}
                                value={
                                  getCommercialPrograms(
                                    secondaryInstitution!
                                  )?.find(
                                    (v) =>
                                      v.id ===
                                      item.outsideEnrollmentAreaCommercialProgramId
                                  )?.name ?? "-"
                                }
                                plaintext
                                bold
                              />
                            </Col>
                          </Row>
                        )}
                    </FormCard>
                    <FormCard cardTitle={t("request.programSelection")}>
                      <Row>
                        <Col md={6}>
                          <TextField
                            label={t(
                              "common.institutionDetails.obavezniStraniJezik"
                            )}
                            value={
                              item.mandatoryForeignLanguages?.find(
                                (v) => v.parentId === secondaryInstitution?.id
                              )?.name
                            }
                            plaintext
                            bold
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <TextField
                            label={t(
                              "common.institutionDetails.izborniPredmeti"
                            )}
                            value={getElectiveSubjectsString(
                              secondaryInstitution?.id
                            )}
                            plaintext
                            bold
                          />
                        </Col>
                      </Row>
                    </FormCard>
                    <FormCard cardTitle={t("request.contactInformation")}>
                      {secondaryInstitution
                        ? getInstitutionContactInformation(secondaryInstitution)
                        : ""}
                    </FormCard>
                  </>
                )}
              </FormCard>
            </FormCard>
          </Tab>
        )}
        {canShowTabQuestionary() && (
          <Tab
            title={t(
              "common.requestModel.requestTabs.initialQuestionnaireInfo"
            )}
            eventKey={TabKey.InitialQuestionnaireInfo}
          >
            <FormCard cardTitle={t("request.initialConversationStep")}>
              <Row>
                <Col md={12}>
                  <Button
                    size="sm"
                    outline
                    onClick={handleGetQuestionnaireClicked}
                  >
                    {t("request.getQuestionnaire")}
                  </Button>
                </Col>
              </Row>
              {getQuestionnaireDisplay()}
            </FormCard>
          </Tab>
        )}
        <Tab
          title={t("common.requestModel.requestTabs.documentInfo")}
          eventKey={TabKey.DocumentInfo}
        >
          <FormCard cardTitle={t("request.documentationStep")}>
            <FormCard cardTitle={t("request.documentParent")}>
              <Row>
                <Col md={12}>
                  <DocumentList
                    documents={item.parentAttachments ?? []}
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(doc, DocumentType.Parent)
                    }
                    removable={false}
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.documentChild")}>
              <Row>
                <Col md={12}>
                  <DocumentList
                    documents={item.childAttachments ?? []}
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(doc, DocumentType.Child)
                    }
                    removable={false}
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.documentMedical")}>
              <Row>
                <Col md={12}>
                  <DocumentList
                    documents={item.medicalAttachments ?? []}
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(doc, DocumentType.Medical)
                    }
                    removable={false}
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.documentPsychology")}>
              <Row>
                <Col md={12}>
                  <DocumentList
                    documents={item.psychologyAttachments ?? []}
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(doc, DocumentType.Psychology)
                    }
                    removable={false}
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.documentEducation")}>
              <Row>
                <Col md={12}>
                  <DocumentList
                    documents={item.educationalRehabilitationAttachments ?? []}
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(
                        doc,
                        DocumentType.EducationalRehabilitation
                      )
                    }
                    removable={false}
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.documentOther")}>
              <Row>
                <Col md={12}>
                  <DocumentList
                    documents={item.otherAttachments ?? []}
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(doc, DocumentType.OtherParent)
                    }
                    removable={false}
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard
              cardTitle={t("request.documentPsychophysicalConditionDecision")}
            >
              <Row>
                <Col md={12}>
                  <DocumentList
                    documents={
                      item.psychophysicalConditionDecisionAttachments ?? []
                    }
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(
                        doc,
                        DocumentType.PsychophysicalConditionDecision
                      )
                    }
                    removable={false}
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.documentUniqueBodyOfExpertise")}>
              <Row>
                <Col md={12}>
                  <DocumentList
                    documents={item.uniqueBodyOfExpertiseAttachments ?? []}
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(
                        doc,
                        DocumentType.UniqueBodyOfExpertise
                      )
                    }
                    removable={false}
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.documentSubsequentlyAttached")}>
              <Row>
                <Col md={4}>
                  <DocumentList
                    documents={item.subsequentAttachments ?? []}
                    onDocumentClick={(doc) =>
                      handleDocumentDownload(doc, DocumentType.Subsequent)
                    }
                    onRemoveClick={(doc) =>
                      handleRemoveSubsequentAttachment(doc)
                    }
                    removable={true} //ovo će bit true
                  />
                </Col>
                <Col md={4}>
                  <Button
                    color="primary"
                    onClick={handleSubsequentUploadDocumentClick}
                    size="sm"
                  >
                    {t("request.documentUpload")}
                  </Button>
                </Col>
                <Col md={4}>
                  <DocumentList
                    documents={item.subsequentUploadFiles ?? []}
                    onRemoveClick={(doc) =>
                      handleRemoveSubsequentUploadFile(doc)
                    }
                    removable={true}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Button
                    color="primary"
                    onClick={handleSubsequentDocumentsSave}
                    size="sm"
                  >
                    {t("request.saveRequestButton")}
                  </Button>
                </Col>
              </Row>
            </FormCard>
          </FormCard>
        </Tab>
        {canShowTabCommitteeDecisionsInstitution() && (
          <Tab
            title={getInstitutionCommitteeTabTitle()}
            eventKey={TabKey.InstitutionCommitteeDecisionInfo}
            className={"text-danger"}
          >
            <CommitteeDecisionWrapper
              item={item}
              committeeDecisions={committeeDecisions}
              committeeType={CommitteeTypeEnum.Institution}
              id={Number(id)}
              setHasGotMinimumDecisions={setHasGotMinimumDecisions}
              onSaveOuterState={savePageState}
            />
          </Tab>
        )}
        {canShowTabCommitteeDecisionsCounty() && (
          <Tab
            title={getCountyCommitteeTabTitle()}
            eventKey={TabKey.CountyCommitteeDecisionInfo}
          >
            <CommitteeDecisionWrapper
              item={item}
              committeeDecisions={committeeDecisions}
              committeeType={CommitteeTypeEnum.County}
              id={Number(id)}
              setHasGotMinimumDecisions={setHasGotMinimumDecisions}
              onSaveOuterState={savePageState}
            />
          </Tab>
        )}
        <Tab
          title={t("common.requestModel.requestTabs.medicalExaminationInfo")}
          eventKey={TabKey.MedicalExaminationInfo}
        >
          <FormCard cardTitle={t("common.requestModel.medicalExamination")}>
            <Row>
              <Col md={3}>{t("medicalExamination.isDone")}</Col>
              <Col md={6}>
                <Select
                  value={
                    medicalExamination?.isDone !== undefined
                      ? medicalExamination.isDone === true
                        ? 2
                        : 1
                      : undefined
                  }
                  selectData={codeBookToSelectData([
                    { id: 1, name: t("common.no") },
                    { id: 2, name: t("common.yes") },
                  ])}
                  emptyOption={{
                    text: "--" + t("common.select") + "--",
                    value: undefined,
                  }}
                  onChange={() => {}}
                  disabled={true}
                />
              </Col>
              <Col md={3}>
                {/* if we have a medicalExamination, anyone can view it */}
                {/* if we don't, only CAS, Helpdesk, Institution Admins or SP president can create */}
                {(medicalExamination ||
                  ((hasRoleAccess(REQUEST_DETAILS_INSTITUTION_ACCESS) ||
                    (item.institutionCommitteeId &&
                      auth
                        .getCommitteeMembershipByCommitteeId(
                          item.institutionCommitteeId
                        )
                        ?.some(
                          (v) =>
                            v.isPresident ||
                            v.committeeRoleId === CommitteeRoleEnum.Physician
                        ))) &&
                    (schoolYear?.isCurrent ?? false))) && (
                  <ArrowButton
                    ariaLabel={t("common.details")}
                    onClick={() =>
                      handleMedicalExaminationDetailsClicked(item.id)
                    }
                  />
                )}
              </Col>
            </Row>
            <Row>
              <Col md={3}>{t("medicalExamination.examinationDate")}</Col>
              <Col md={9}>
                <DatePicker
                  value={medicalExamination?.examinationDate}
                  plaintext={true}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3}>{t("medicalExamination.note")}</Col>
              <Col md={9}>
                <TextArea
                  ariaLabel={t("medicalExamination.note")}
                  readonly={true}
                  value={medicalExamination?.note}
                />
              </Col>
            </Row>
          </FormCard>

          <FormCard cardTitle={t("common.requestModel.attachments")}>
            <Row>
              <Col md={4}>
                <DocumentList
                  documents={medicalExamination?.attachments ?? []}
                  onDocumentClick={(doc) =>
                    handleDocumentDownload(doc, DocumentType.MedicalExamination)
                  }
                  removable={false}
                />
              </Col>
            </Row>
          </FormCard>
        </Tab>
      </Tabs>
      <DocumentsModal
        title={t("accessibility.documentModal.title")}
        infoPanelText={t("accessibility.documentModal.info")}
        types={t("accessibility.documentModal.types")}
        allDocuments={uploadDocument ?? []}
        maxSize={10}
        filesAction={handleFileChange}
        open={showUploadModal !== undefined}
        onConfirm={handleUploadFileChange}
        onClose={() => {
          setUploadDocument([]);
          setShowUploadModal(undefined);
        }}
        hideOldDocuments={true}
        btnCancelText={t("common.quit")}
        btnOkText={t("common.addNew")}
      />
      <FormCard>
        <Row>
          <Col md={12}>
            {/* Ispis zahtjeva - Form6 */}
            {(((enrollmentState.showForm6Button || !!form6) &&
              isStatusNotSuspended) ||
              (!isStatusNotSuspended && !!form6)) && (
              <Button
                className="mr-2"
                size="sm"
                onClick={handleGenerateForm6Clicked}
              >
                {t("request.generateForm6")}
              </Button>
            )}
            {/* Dodaj školskog obveznika */}
            {((hasRoleAccess(REQUEST_DETAILS_ADMIN_ACCESS) &&
              isStatusNotSuspended) ||
              auth.currentRole?.claimType === ClaimType.County) &&
              item.type === RequestType.EarlyEnrollment &&
              item.statusId === RequestStatus.ProcedureSubmitted &&
              (schoolYear?.isCurrent ?? false) && (
                <Button
                  className="mr-2"
                  size="sm"
                  onClick={handleAddSchoolCandidateClicked}
                >
                  {t("request.addSchoolCandidate")}
                </Button>
              )}
            {/* Vrati na obradu */}
            {((hasRoleAccess(REQUEST_DETAILS_ADMIN_ACCESS) &&
              isStatusNotSuspended) ||
              [ClaimType.Institution, ClaimType.County].includes(
                auth.currentRole?.claimType ?? ClaimType.Committee
              )) &&
              [
                RequestStatus.ProcedureSubmitted,
                RequestStatus.Submitted,
              ].includes(item.statusId) &&
              (schoolYear?.isCurrent ?? false) && (
                <Button
                  className="mr-2"
                  size="sm"
                  onClick={handleReturnToProcessingClicked}
                >
                  {t("request.returnToProcessing")}
                </Button>
              )}
            {/* Izmjena zahtjeva u Redovan upis */}
            {((hasRoleAccess(REQUEST_DETAILS_ADMIN_ACCESS) &&
              isStatusNotSuspended) ||
              // Only first institution
              ([
                Role.InstitutionAdministrator,
                Role.InstitutionPrincipal,
              ].includes(auth.currentRole?.role ?? 0) &&
                item &&
                auth.currentRole?.locations?.find(
                  (v) => v === primaryInstitution?.id
                ))) &&
              item.statusId === RequestStatus.Submitted &&
              item.type ===
                RequestType.RegularEnrollmentForChildrenWithLearningDifficulties &&
              (schoolYear?.isCurrent ?? false) && (
                <Button
                  className="mr-2"
                  size="sm"
                  onClick={handleChangeRequestType}
                >
                  {t("request.changeTypeButton")}
                </Button>
              )}
            {/* Select institution data */}
            {((hasRoleAccess(REQUEST_DETAILS_ADMIN_ACCESS) &&
              isStatusNotSuspended) ||
              auth.currentRole?.claimType === ClaimType.Institution) &&
              item.statusId === RequestStatus.OnSchoolCandidateList &&
              (schoolYear?.isCurrent ?? false) && (
                <Button
                  className="mr-2"
                  size="sm"
                  onClick={handleSelectInstitutionDataClicked}
                >
                  {t("request.selectInstitutionData")}
                </Button>
              )}
            {/* Storno */}
            {(hasRoleAccess(REQUEST_DETAILS_INSTITUTION_ACCESS) ||
              hasRoleAccess({
                whitelist: [Role.CountyAdministrator, Role.CountyLeader],
              })) &&
              [
                RequestType.EarlyEnrollment,
                RequestType.TemporaryExemption,
              ].includes(item.type) &&
              [
                RequestStatus.ProcedureSubmitted,
                RequestStatus.OnSchoolCandidateList,
              ].includes(item.statusId) &&
              (schoolYear?.isCurrent ?? false) && (
                <Button
                  className="mr-2"
                  size="sm"
                  onClick={handleCancelClicked}
                >
                  {t("request.cancelRequest")}
                </Button>
              )}
            {/* Generiraj mišljenje s prijedlogom */}
            {((isStatusNotSuspended &&
              (userIsCommitteePresident ||
                (hasRoleAccess(REQUEST_DETAILS_INSTITUTION_ACCESS) &&
                  [
                    RequestStatus.Submitted,
                    RequestStatus.OpinionWithRecommendation,
                  ].includes(item.statusId) &&
                  [
                    RequestType.RegularEnrollment,
                    RequestType.RegularEnrollmentForChildrenWithLearningDifficulties,
                    RequestType.EarlyEnrollment,
                  ].includes(item.type) &&
                  (schoolYear?.isCurrent ?? false)))) ||
              decisionProposal) && (
              <Button
                className="mr-2"
                size="sm"
                onClick={handleGenerateDecisionProposalClicked}
                disabled={!hasGotMinimumDecisions.institution}
                outline={!!form7}
              >
                {t("request.generateDecisionWithProposal")}
              </Button>
            )}
            {/* Obrazac 7 */}

            {((isStatusNotSuspended &&
              (hasRoleAccess(REQUEST_DETAILS_COUNTY_ACCESS) ||
                userIsCommitteePresident ||
                (item.countyCommitteeId &&
                  auth
                    .getCommitteeMembershipByCommitteeId(item.countyCommitteeId)
                    ?.some((v) => v.isPresident))) &&
              ((item.type !== RequestType.TemporaryExemption &&
                item.statusId === RequestStatus.OpinionWithRecommendation) ||
                (item.type === RequestType.TemporaryExemption &&
                  item.statusId === RequestStatus.ProcedureSubmitted)) &&
              (schoolYear?.isCurrent ?? false)) || // create or edit
              form7) && (
              <Button
                className="mr-2"
                size="sm"
                onClick={handleGenerateForm7Clicked}
                disabled={!hasGotMinimumDecisions.county}
                outline={!!enrollmentDecision}
              >
                {t("request.generateForm7")}
              </Button>
            )}
            {/* Rješenje o upisu */}
            {((isStatusNotSuspended &&
              (hasRoleAccess(REQUEST_DETAILS_COUNTY_ACCESS) ||
                (item.countyCommitteeId &&
                  auth
                    .getCommitteeMembershipByCommitteeId(item.countyCommitteeId)
                    ?.some((v) => v.isPresident))) &&
              form7 &&
              (item.statusId === RequestStatus.OpinionWithRecommendation ||
                (item.type === RequestType.TemporaryExemption &&
                  item.statusId === RequestStatus.ProcedureSubmitted)) &&
              (schoolYear?.isCurrent ?? false)) || // create or edit
              enrollmentDecision) && (
              <Button
                className="mr-2"
                size="sm"
                onClick={handleEnrollmentDecisionClicked}
                outline={!!enrollmentDecision}
              >
                {t("request.getDecision")}
              </Button>
            )}
            {/* Rješenje o obustavi postupka */}
            {enrollmentState.showSuspensionDecisionButton &&
              isStatusNotSuspended &&
              (item.type === RequestType.TemporaryExemption ||
                item.type === RequestType.EarlyEnrollment) && (
                <Button
                  className="mr-2"
                  size="sm"
                  onClick={handleSuspensionDecisionClicked}
                >
                  {t("requestStatus.SuspensionDecision")}
                </Button>
              )}
            {/* Izmjene škole u koju se dijete upisuje - Enrollment Institution */}
            {hasRoleAccess(REQUEST_DETAILS_COUNTY_ACCESS) &&
              isStatusNotSuspended &&
              ENROLLMENT_INSTITUTION_STATUSES.includes(item.statusId) &&
              [
                RequestType.EarlyEnrollment,
                RequestType.RegularEnrollment,
                RequestType.RegularEnrollmentForChildrenWithLearningDifficulties,
              ].includes(item.type) &&
              (schoolYear?.isCurrent ?? false) && (
                <Button
                  className="mr-2"
                  size="sm"
                  onClick={handleEnrollmentInstitutionClicked}
                  disabled={
                    item.synchronizationStatus ===
                    SynchronizationStatus.RegisteredInEMatica
                  }
                  outline={false}
                >
                  {t("request.changeEnrollmentInstitution")}
                </Button>
              )}
            {/* Odobri postupak upisa */}
            {((hasRoleAccess(REQUEST_DETAILS_INSTITUTION_ACCESS) &&
              isStatusNotSuspended) ||
              userIsCommitteePresident) &&
              (schoolYear?.isCurrent ?? false) &&
              item.statusId === RequestStatus.Submitted &&
              item.type === RequestType.RegularEnrollment && (
                <Button
                  className="mr-2"
                  size="sm"
                  onClick={handleApproveClicked}
                  disabled={
                    !hasGotMinimumDecisions.institution ||
                    enrollmentState.isEnrollmentApprovedButtonDisabled
                  }
                >
                  {t("request.approveRequest")}
                </Button>
              )}
          </Col>
        </Row>
        {/* Submitted or ProcedureSubmitted */}
        {hasRoleAccess(REQUEST_DETAILS_RETURN_TO_ACCESS) &&
          !RETURN_TO_SUBMITTED_STATUSES.includes(item.statusId) &&
          (schoolYear?.isCurrent ?? false) && (
            <>
              <Row className={styles.rowPaddingMedium}>
                <Col md={12}>
                  <Button
                    className="mr-2"
                    size="sm"
                    color="danger"
                    onClick={handleSubmittedClicked}
                    disabled={
                      item.synchronizationStatus ===
                      SynchronizationStatus.RegisteredInEMatica
                    }
                  >
                    {RETURN_TO_PROCEDURE_STATUSES.includes(item.statusId)
                      ? t("request.returnToProcedureSubmitted")
                      : t("request.returnToSubmitted")}
                  </Button>
                </Col>
              </Row>
            </>
          )}
        {hasRoleAccess(REQUEST_DETAILS_ADMIN_ACCESS) && (
          <StatusHistoryActionButtonModal
            statusHistory={item.statusHistory}
            className="mt-2"
          />
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"hide"}
        onClickCancel={goBack}
      />
      <Modal
        type={"message"}
        confirmText={t("common.close")}
        open={actionMessage !== undefined}
        onConfirm={handleMessageOK}
        onClose={() => {
          setActionMessage(undefined);
        }}
      >
        {actionMessage}
      </Modal>
      <Modal
        type={"confirm"}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        open={confirmMessage !== undefined}
        onConfirm={handleConfirmOk}
        onClose={handleConfirmCancel}
      >
        {confirmMessage}
        {confirmMessage2 && (
          <Typography variant="mediumBold" color="danger">
            {confirmMessage2}
          </Typography>
        )}
        {confirmAction === "submitted" &&
          hasRoleAccess({ whitelist: [Role.CAS, Role.Helpdesk] }) && (
            <TextAreaWithCount
              className="mt-3"
              ariaLabel={t("request.returnToSubmittedComment")}
              label={t("request.returnToSubmittedComment")}
              name="returnToSubmittedComment"
              value={returnToSubmittedComment}
              onChange={(value) => setReturnToSubmittedComment(value)}
              rows={3}
              maxLength={500}
              errorText={
                confirmMessage !== undefined &&
                !returnToSubmittedComment?.trim()
                  ? t("validations.required")
                  : undefined
              }
            />
          )}
      </Modal>
      <ConfirmModal
        title={""}
        showCloseMark={false}
        confirmState={changeTypeConfirmState}
        confirmMessage={t("request.changeTypeConfirmationTitle")}
        successMesage={t("common.saveSuccess")}
        errorMessage={t("common.error")}
        onClickConfirm={handleConfirmOk}
        onClickClose={handleConfirmCancel}
      >
        <TextAreaWithCount
          ariaLabel={t("request.changeTypeComment")}
          name={"changeTypeComment"}
          value={changeTypeComment}
          onChange={(value) => setChangeTypeComment(value)}
          rows={4}
          maxLength={750}
          autoAdjustRows={false}
        />
        <Typography>
          {t("request.changeTypeConfirmationCommentNote")}
        </Typography>
      </ConfirmModal>
      <RemarksRedirectModal
        open={showRemarksModal}
        onConfirm={handleRemarksModalConfirm}
        onClose={handleRemarksModalClose}
        pendingAction={pendingAction}
      />
    </ContentArea>
  );
}
